import { useEffect, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import posthog from 'posthog-js';
import { PostHogProvider as PHProvider } from 'posthog-js/react';

interface PostHogProviderProps {
  children: React.ReactNode;
}

// Capability tokens (document-share /share/:token, check-in, etc.) are 32+ hex
// chars in the URL path. Redact any such run from URL-valued analytics properties
// so a bearer secret never lands in events, session replay, or exceptions. Shared
// with the Sentry init via src/lib/scrubTokens.
export { scrubTokenUrlProps as redactCapabilityTokens } from '@/lib/scrubTokens';
import { hireTelemetry, isHireSurface } from '@/lib/hirePrivacy';
import { scrubTokenUrlProps } from '@/lib/scrubTokens';

// Initialize PostHog
if (typeof window !== 'undefined') {
  const apiKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
  const apiHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com';

  // Skip PostHog on demo environment to avoid 401 errors
  const hostname = window.location.hostname;
  const isDemo = hostname === 'demo.hollywoodtrucks.com';

  // Only initialize if API key is provided and NOT on demo environment
  if (apiKey && !isDemo) {
    try {
      posthog.init(apiKey, {
        api_host: apiHost,
        person_profiles: 'identified_only',
        before_send: event => hireTelemetry(event),
        session_recording: { blockClass: 'hire-private', maskAllInputs: true },
        sanitize_properties: scrubTokenUrlProps, // keep capability tokens out of analytics
        capture_pageview: false, // Disable automatic pageview capture, we'll capture manually
        capture_pageleave: true,
        capture_exceptions: true, // Autocapture JS exceptions
        debug: false, // Always disable debug in production
        loaded: (_posthog) => {
          // Only enable if loaded successfully
          if (import.meta.env.MODE === 'development') {
            console.log('PostHog loaded successfully');
          }
        },
        // Session recording with console log capture
        disable_session_recording: isHireSurface(window.location.pathname),
        enable_recording_console_log: true,
        disable_persistence: false, // Keep local persistence
        persistence: 'localStorage',
        // Disable feature flags and surveys to prevent config.js MIME type errors
        advanced_disable_feature_flags: true,
        advanced_disable_feature_flags_on_first_load: true,
        disable_surveys: true,
        // Handle errors silently
        _onCapture: undefined,
      });
    } catch (error) {
      // Silently fail - don't break the app if PostHog has issues
      console.warn('PostHog initialization failed:', error);
    }
  } else {
    if (import.meta.env.MODE === 'development') {
      console.warn('PostHog API key not found. Analytics will be disabled.');
    }
  }
}

export function PostHogProvider({ children }: PostHogProviderProps) {
  const location = useLocation();

  useLayoutEffect(() => {
    if (!posthog.__loaded) return;
    if (isHireSurface(location.pathname)) posthog.stopSessionRecording();
    else posthog.startSessionRecording();
  }, [location.pathname]);

  useEffect(() => {
    if (isHireSurface(location.pathname)) return;
    // Capture pageview on mount and route changes
    // PostHog SDK queues events until initialized, no need to check __loaded
    if (typeof window !== 'undefined' && posthog && posthog.__loaded) {
      try {
        posthog.capture('$pageview');
      } catch (error) {
        // Silently fail - analytics shouldn't break the app
        if (import.meta.env.MODE === 'development') {
          console.warn('PostHog capture failed:', error);
        }
      }
    }
  }, [location]);

  // If PostHog didn't initialize (missing API key), render children without provider
  const apiKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
  if (!apiKey || !posthog) {
    return <>{children}</>;
  }

  return <PHProvider client={posthog}>{children}</PHProvider>;
}
